/**
 * Iter XX.243c · Ranked People Discovery — primary suggestions,
 * secondary name search, honest empty-state.
 *
 * Layout:
 *   1. "Cricketers to follow" strip — ALWAYS visible when eligible
 *      users exist. Ranked server-side by (has_moments, role_match,
 *      recency). Excludes self + already-followed.
 *   2. "Find someone you know" search — collapsible, secondary role.
 *   3. Honest empty state when zero users are eligible: "Gully Pulse
 *      is just getting started. Create your cricket profile and be
 *      one of the first cricketers here." Never leaves the panel
 *      blank without explanation.
 *
 * The search input's empty-state message now shows the user's
 * ACTUAL typed query (previously the response echoed an empty
 * query string, producing 'No cricketers matched ""').
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  searchCricketers, suggestCricketers,
  followUserFromDiscovery, unfollowUser,
} from "../lib/pulse";

function CricketerCard({ item, source, onChanged, compact = false }) {
  const navigate = useNavigate();
  const [following, setFollowing] = useState(!!item.is_following);
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy || item.is_self) return;
    setBusy(true);
    if (following) {
      await unfollowUser(item.user_id);
      setFollowing(false);
    } else {
      const res = await followUserFromDiscovery(item.user_id, source);
      if (!res?.error) setFollowing(true);
    }
    setBusy(false);
    onChanged && onChanged();
  };
  return (
    <div
      data-testid="cricketer-card"
      onClick={() => navigate(`/pulse/u/${item.user_id}`)}
      className={
        "flex items-center gap-3 p-3 rounded-2xl border border-neutral-800 bg-neutral-900/70 cursor-pointer hover:bg-neutral-900 transition-colors " +
        (compact ? "min-w-[220px]" : "")
      }
    >
      <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-300 font-medium shrink-0">
        {(item.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div data-testid="cricketer-card-name" className="text-[14px] text-neutral-100 font-medium truncate">
          {item.name}
        </div>
        {(item.role || item.team || item.location) && (
          <div data-testid="cricketer-card-meta" className="text-[12px] text-neutral-500 truncate">
            {[item.role, item.team, item.location].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      {!item.is_self && (
        <button
          data-testid="cricketer-card-follow"
          onClick={toggle}
          disabled={busy}
          className={
            "px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors shrink-0 " +
            (following
              ? "bg-neutral-800 text-neutral-100 hover:bg-neutral-700"
              : "bg-emerald-500 text-neutral-950 hover:bg-emerald-400")
          }
        >
          {following ? "Following" : "Follow"}
        </button>
      )}
    </div>
  );
}

function SuggestionsStrip({ suggestions, onChanged }) {
  return (
    <div
      data-testid="pulse-suggestions"
      className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x"
    >
      {suggestions.map((it) => (
        <div key={it.user_id} className="snap-start">
          <CricketerCard
            item={it}
            source="discovery_suggestions"
            onChanged={onChanged}
            compact
          />
        </div>
      ))}
    </div>
  );
}

export default function PeopleDiscovery({ onCreateProfile }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [eligibleTotal, setEligibleTotal] = useState(null);
  const debounceRef = useRef(null);

  const reloadSuggestions = async () => {
    const res = await suggestCricketers(10);
    setSuggestions(res.items || []);
    setEligibleTotal(typeof res.eligible_total === "number" ? res.eligible_total : null);
  };
  useEffect(() => { reloadSuggestions(); }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const qc = q.trim();
    if (qc.length < 2) { setResults(null); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const res = await searchCricketers(qc, 20);
      // XX.243c bugfix — carry the user's ACTUAL query for the
      // empty-state copy. The API's `query` field can be blank in
      // edge cases; we always show what they typed.
      setResults({ ...res, typed: qc });
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Primary suggestion strip is empty AND server confirms 0 eligible.
  const showJustStarting =
    suggestions.length === 0 && eligibleTotal === 0;

  return (
    <section data-testid="pulse-people-discovery" className="mb-6">
      {/* PRIMARY — Cricketers to follow */}
      <div className="text-[13px] text-neutral-300 font-medium mb-3">
        Cricketers to follow
      </div>

      {suggestions.length > 0 && (
        <SuggestionsStrip
          suggestions={suggestions}
          onChanged={reloadSuggestions}
        />
      )}

      {showJustStarting && (
        <div
          data-testid="pulse-discovery-just-starting"
          className="rounded-3xl border border-emerald-900/40 bg-emerald-900/10 p-5 mb-4"
        >
          <div className="text-[16px] text-neutral-100 font-medium mb-1">
            Gully Pulse is just getting started.
          </div>
          <p className="text-[13px] text-neutral-400 mb-3">
            Create your cricket profile and be one of the first cricketers here.
          </p>
          {onCreateProfile && (
            <button
              data-testid="pulse-discovery-just-starting-cta"
              onClick={onCreateProfile}
              className="px-4 py-2 rounded-full bg-emerald-500 text-neutral-950 text-[13px] font-medium hover:bg-emerald-400 transition-colors"
            >
              Create my cricket profile
            </button>
          )}
        </div>
      )}

      {/* SECONDARY — Find someone you know */}
      <div className="mt-6">
        <div className="text-[13px] text-neutral-400 mb-2">
          Find someone you know
        </div>
        <input
          data-testid="pulse-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name…"
          maxLength={60}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-full px-4 py-2.5 text-[14px] text-neutral-100 focus:outline-none focus:border-emerald-800"
        />
        {results && (
          <div data-testid="pulse-search-results" className="space-y-2 mt-3">
            {searching && (
              <div className="text-[13px] text-neutral-500 py-2">Searching…</div>
            )}
            {!searching && results.items.length === 0 && (
              <div
                data-testid="pulse-search-empty"
                className="text-[13px] text-neutral-500 py-2"
              >
                No cricketers matched "{results.typed}".
              </div>
            )}
            {results.items.map((it) => (
              <CricketerCard
                key={it.user_id}
                item={it}
                source="discovery_search"
                onChanged={reloadSuggestions}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
